import React, { useEffect, useRef } from 'react'

/**
 * Custom hook to handle Google Translate initialization
 * Loads the Google Translate script dynamically and initializes the widget
 */
const useGoogleTranslate = () => {
  const isInitialized = useRef(false)
  const scriptLoaded = useRef(false)

  useEffect(() => {
    // Prevent multiple initializations
    if (isInitialized.current) return

    const initializeGoogleTranslate = () => {
      try {
        if (typeof window !== 'undefined' && window.google?.translate) {
          new window.google.translate.TranslateElement(
            {
              pageLanguage: 'auto',
              layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
              autoDisplay: false,
              // Removed includedLanguages to show ALL available languages
            },
            'google_translate_element',
          )

          isInitialized.current = true
        }
      } catch (error) {
        console.warn('Google Translate failed to initialize:', error)
      }
    }

    const loadGoogleTranslateScript = () => {
      // Check if script is already loaded
      if (scriptLoaded.current) {
        initializeGoogleTranslate()
        return
      }

      // Create script element
      const script = document.createElement('script')
      script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit'
      script.async = true
      script.defer = true

      // Define the callback function
      window.googleTranslateElementInit = initializeGoogleTranslate

      // Add script to document
      document.head.appendChild(script)
      scriptLoaded.current = true

      // Fallback if script fails to load
      script.onerror = () => {
        console.warn('Google Translate script failed to load')
      }
    }

    // Load script when component mounts
    loadGoogleTranslateScript()

    // Cleanup function
    return () => {
      // Remove the callback function
      if (window.googleTranslateElementInit) {
        delete window.googleTranslateElementInit
      }
    }
  }, [])
}

/**
 * Google Translate component that integrates with Google Translate API
 * Provides automatic translation of the entire page content
 * Uses a custom React hook for better integration
 */
export const GoogleTranslate: React.FC = () => {
  useGoogleTranslate()

  return (
    <div className="google-translate-container">
      {/* Google Translate element - populated by the hook */}
      <div id="google_translate_element" className="google-translate-element" />
    </div>
  )
}

// TypeScript declarations for Google Translate
declare global {
  interface Window {
    google?: {
      translate?: {
        TranslateElement: {
          new (options: any, elementId: string): any
          InlineLayout: {
            SIMPLE: any
          }
        }
      }
    }
    googleTranslateElementInit?: () => void
  }
}
